import type { FC } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import Button from '@atlaskit/button/default/button';
import { token } from '@atlaskit/tokens';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';

import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';

import { AiPolicyFooter } from './AiPolicyFooter.tsx';

type AISuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

const i18n = defineMessages({
	acceptAll: {
		id: 'suggestions-panel.suggestions-footer.accept-all',
		defaultMessage: 'Accept all',
		description: 'Button label to accept all AI suggestions',
	},
	discardAll: {
		id: 'suggestions-panel.suggestions-footer.discard-all',
		defaultMessage: 'Discard all',
		description: 'Button label to discard all AI suggestions',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	footer: {
		display: 'flex',
		gap: token('space.100'),
	},
	buttonContainer: {
		flex: 1,
	},
});

/**
 * This component is only used under platform_editor_ai_content_suggested_edits
 * at the moment.
 */
export const SuggestionsFooter: FC = () => {
	const { formatMessage } = useIntl();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const { hasSuggestions } = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		(state: { aiSuggestionsState?: AISuggestionsSharedState }) => ({
			hasSuggestions: (state?.aiSuggestionsState?.orderedSuggestionIds?.length ?? 0) > 0,
		}),
	);

	const handleAcceptAll = useCallback(() => {
		const actions = fullPageEditorPresetAPI?.aiSuggestions?.actions;
		actions?.acceptAllSuggestions();
	}, [fullPageEditorPresetAPI]);

	const handleDiscardAll = useCallback(() => {
		const actions = fullPageEditorPresetAPI?.aiSuggestions?.actions;
		actions?.discardAllSuggestions();
	}, [fullPageEditorPresetAPI]);

	return (
		<div css={styles.wrapper}>
			{hasSuggestions && (
				<div css={styles.footer}>
					<div css={styles.buttonContainer}>
						<Button onClick={handleAcceptAll} shouldFitContainer>
							{formatMessage(i18n.acceptAll)}
						</Button>
					</div>
					<div css={styles.buttonContainer}>
						<Button onClick={handleDiscardAll} shouldFitContainer>
							{formatMessage(i18n.discardAll)}
						</Button>
					</div>
				</div>
			)}
			<AiPolicyFooter />
		</div>
	);
};
